/**
 * =============================================================================
 * COMPONENT:  MiniJourney.js
 * FUNKTION:   User-Journey Zusammenfassung pro Kampagne im Ads Dashboard
 * VERSION:    v1.0
 * DATUM:      2026-03-04
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import './MiniJourney.css';

const CONVERSION_ORDER = ['Coaching', 'SG geführt', 'SG gebucht', 'EG geführt', 'EG gebucht', 'Webinar', '—'];
const CONVERSION_CLS = {
    'Coaching':   'conv-coaching',
    'SG geführt': 'conv-sg',
    'SG gebucht': 'conv-sg',
    'EG geführt': 'conv-eg',
    'EG gebucht': 'conv-eg',
    'Webinar':    'conv-webinar',
    '—':          'conv-none',
};

export default function MiniJourney({ von1, bis1, campaignIds }) {
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [expanded, setExpanded]           = useState(null);
    const [expandedEvents, setExpandedEvents] = useState(null);
    const [events, setEvents]                 = useState({});
    const [eventsLoading, setEventsLoading]   = useState(null);
    const token = localStorage.getItem('token') || '';

    const load = useCallback(async () => {
        if (!campaignIds || campaignIds.length === 0) return;
        setLoading(true);
        setError('');
        try {
            const url = `/api/ads_journey_report.php?von=${von1}&bis=${bis1}&campaign_ids=${campaignIds.join(',')}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const json = await res.json();
            if (json.success) setData(json.data);
            else setError(json.message || 'Fehler');
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    }, [von1, bis1, campaignIds, token]);

    useEffect(() => { load(); }, [load]);

    const loadEvents = async (visitorId, idx) => {
        if (expanded === idx) {
            setExpanded(null);
            setExpandedEvents(null);
            return;
        }
        setExpanded(idx);
        setExpandedEvents(null);
        setEventsLoading(null);
    };

    const loadEventsDetail = async (visitorId, idx) => {
        if (expandedEvents === idx) {
            setExpandedEvents(null);
            return;
        }
        setExpandedEvents(idx);
        if (events[idx]) return;
        setEventsLoading(idx);
        try {
            const url = `/api/ads_journey_report.php?visitor_id=${encodeURIComponent(visitorId)}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const json = await res.json();
            if (json.success) setEvents(prev => ({ ...prev, [idx]: json.events }));
        } catch (e) { console.error(e); }
        setEventsLoading(null);
    };

    // Zusammenfassung
    const total       = data.length;
    const conversions = data.filter(r => r.conversion !== '—').length;
    const convRate    = total > 0 ? ((conversions / total) * 100).toFixed(1) : 0;

    // Conversion-Verteilung
    const convDist = CONVERSION_ORDER.filter(c => c !== '—').reduce((acc, c) => {
        acc[c] = data.filter(r => r.conversion === c).length;
        return acc;
    }, {});

    return (
        <div className="mj-wrap">
            <div className="mj-header">
                <span className="mj-title">👣 User Journeys</span>
                {!loading && total > 0 && (
                    <div className="mj-summary">
                        <span className="mj-stat">{total} Besucher</span>
                        <span className="mj-stat mj-conv">{conversions} Conversions ({convRate}%)</span>
                        {Object.entries(convDist).filter(([,v]) => v > 0).map(([k, v]) => (
                            <span key={k} className={`mj-badge ${CONVERSION_CLS[k]}`}>{k}: {v}</span>
                        ))}
                    </div>
                )}
            </div>

            {loading && <p className="mj-loading">Lädt…</p>}
            {error   && <p className="mj-error">{error}</p>}

            {!loading && total === 0 && !error && (
                <p className="mj-empty">Keine Journey-Daten für diesen Zeitraum und diese Kampagne(n).</p>
            )}

            {!loading && total > 0 && (
                <div className="mj-table-wrap">
                    <table className="mj-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Gerät</th>
                                <th>Dauer</th>
                                <th>Keyword</th>
                                <th>Einstieg</th>
                                <th>Letzte Sektion</th>
                                <th>CTA</th>
                                <th>Conversion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((r, i) => {
                                const isExpanded = expanded === i;
                                const convCls    = CONVERSION_CLS[r.conversion] || 'conv-none';
                                const hasConv    = r.conversion !== '—';
                                return (
                                    <React.Fragment key={i}>
                                        <tr
                                            className={`mj-row ${hasConv ? 'mj-row-conv' : ''} ${isExpanded ? 'mj-row-expanded' : ''}`}
                                            onClick={() => loadEvents(r.visitor_id, i)}
                                        >
                                            <td className="mj-nr">{i + 1}</td>
                                            <td className="mj-name">{r.name}</td>
                                            <td className="mj-device">{r.geraet || '—'}</td>
                                            <td className="mj-dauer">{r.dauer}</td>
                                            <td className="mj-kw">{r.keyword || '—'}</td>
                                            <td className="mj-lp">{r.einstieg_lp || '—'}</td>
                                            <td className="mj-sektion">{r.letzte_sektion || '—'}</td>
                                            <td className="mj-cta">{Number(r.cta_klicks) > 0 ? `✅ ${r.cta_klicks}` : '—'}</td>
                                            <td><span className={`mj-conv-badge ${convCls}`}>{r.conversion}</span></td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="mj-detail-row">
                                                <td colSpan={9} onClick={e => e.stopPropagation()}>
                                                    <div className="mj-detail-header" onClick={() => loadEventsDetail(r.visitor_id, i)} style={{cursor:'pointer'}}>
                                                        <span><strong>Visitor ID:</strong> {r.visitor_id}</span>
                                                        <span><strong>Kampagne:</strong> {r.kampagne}</span>
                                                        <span><strong>Events:</strong> {r.event_anzahl}</span>
                                                        <span style={{color:'#3b5bdb', fontSize:'.8rem'}}>{expandedEvents === i ? '▲ Journey schließen' : '▼ Journey anzeigen'}</span>
                                                    </div>
                                                    {eventsLoading === i && <p className="mj-loading" style={{padding:'8px 16px'}}>Lädt Events…</p>}
                                                    {expandedEvents === i && events[i] && (
                                                        <div>
                                                            <table className="mj-events-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Zeitpunkt</th>
                                                                        <th>Event</th>
                                                                        <th>LP</th>
                                                                        <th>Sektion</th>
                                                                        <th>CTA</th>
                                                                        <th>Details</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {events[i].map((ev, ei) => (
                                                                        <tr key={ei} className={`mj-ev-row mj-ev-${ev.event_type?.toLowerCase()}`}>
                                                                            <td className="mj-ev-time">{ev.event_timestamp?.replace('T',' ').substring(0,19)}</td>
                                                                            <td className="mj-ev-type">{ev.event_type}</td>
                                                                            <td className="mj-ev-lp">{ev.lp_id || '—'}</td>
                                                                            <td className="mj-ev-sec">{ev.section_name || '—'}</td>
                                                                            <td className="mj-ev-cta">{ev.cta_id || '—'}</td>
                                                                            <td className="mj-ev-det">{ev.event_details ? (() => { try { return JSON.stringify(JSON.parse(ev.event_details)); } catch(e) { return ev.event_details; } })() : '—'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}